import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  toggleLikePost,
  deletePost,
  addComment,
  deleteComment,
  getComments,
  getFeed,
} from '../../lib/services/feedService';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Heart, MessageCircle, Trash2, Send, ThumbsUp, X, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ReactPlayer from 'react-player/youtube';
import DOMPurify from 'dompurify';
import {
  Dialog as AlertDialog,
  DialogTrigger as AlertDialogTrigger,
  DialogContent as AlertDialogContent,
  DialogHeader as AlertDialogHeader,
  DialogTitle as AlertDialogTitle,
  DialogDescription as AlertDialogDescription,
  DialogFooter as AlertDialogFooter,
  DialogClose as AlertDialogCancel,
} from '../ui/dialog';
import { Spinner } from '../Spinner';
import { GetFeedResult, PostTagRow, PostRow } from '../../types/database';
import CreatePostForm from './CreatePostForm';
import { v4 as uuidv4 } from 'uuid';
import PostItem from './PostItem';
import { toast } from 'react-hot-toast';

interface ExternalFilters {
  tagType?: 'game' | 'mechanic' | 'category' | 'theme';
  tagValue?: string;
  filterUserId?: string;
  filterGameName?: string;
  sortBy?: 'created_at_desc' | 'created_at_asc' | 'like_count_desc';
}

interface FeedComponentProps {
  externalFilters?: ExternalFilters;
  onTagClick: (type: 'game' | 'mechanic' | 'category' | 'theme', value: string, displayName?: string) => void;
  limit?: number;
  enableInfiniteScroll?: boolean;
  showCreatePostForm?: boolean;
}

const FeedComponent: React.FC<FeedComponentProps> = ({
  externalFilters = {},
  onTagClick,
  limit = 10,
  enableInfiniteScroll = false,
  showCreatePostForm = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<GetFeedResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastPostRef = useRef<HTMLDivElement | null>(null);

  const fetchFeed = useCallback(async (pageNumber = 1, append = false) => {
    try {
      if (pageNumber === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      console.log('[FeedComponent] fetchFeed chamado com page:', pageNumber, 'e filtros:', externalFilters);

      const feedData = await getFeed(
        user?.id || null,
        {
          filterUserId: externalFilters.filterUserId,
          tagType: externalFilters.tagType,
          tagValue: externalFilters.tagValue,
          filterGameName: externalFilters.filterGameName,
          sortBy: externalFilters.sortBy
        },
        pageNumber,
        limit
      );
      
      if (feedData) {
        if (append) {
          setPosts(prev => [...prev, ...feedData]);
        } else {
          setPosts(feedData);
        }
        setHasMore(feedData.length === limit);
      } else {
        if (!append) setPosts([]);
        setHasMore(false);
      }
      
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar feed:', err);
      setError('Não foi possível carregar o feed. Tente novamente mais tarde.');
      if (!append) setPosts([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user?.id, externalFilters, limit]);

  useEffect(() => {
    console.log('[FeedComponent] useEffect disparado - externalFilters:', externalFilters);
    setPage(1);
    fetchFeed(1, false);
  }, [fetchFeed, externalFilters]);

  useEffect(() => {
    if (!enableInfiniteScroll) return;

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) {
        setPage(prevPage => prevPage + 1);
        fetchFeed(page + 1, true);
      }
    };

    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    });

    if (lastPostRef.current) {
      observer.current.observe(lastPostRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [enableInfiniteScroll, hasMore, loadingMore, page, fetchFeed]);

  const handleToggleLike = useCallback(async (postId: string, authorId: string | null | undefined) => {
    if (!user) {
      toast.error('Você precisa estar logado para curtir/descurtir.');
      return;
    }

    if (!authorId) {
      console.error('Tentativa de curtir post sem authorId:', postId);
      toast.error('Ocorreu um erro ao processar a curtida (autor não encontrado).');
      return;
    }

    const currentPost = posts.find(p => p.id === postId);
    if (!currentPost) return;
    const currentlyLiked = currentPost.user_liked;

    setPosts(prevPosts =>
      prevPosts.map(p =>
        p.id === postId
          ? {
              ...p,
              user_liked: !currentlyLiked,
              like_count: currentlyLiked ? (p.like_count ?? 1) - 1 : (p.like_count ?? 0) + 1,
            }
          : p
      )
    );

    try {
      await toggleLikePost(postId, user.id, authorId);
    } catch (err: any) {
      console.error('Erro ao curtir/descurtir post:', err);
      toast.error(`Falha ao ${currentlyLiked ? 'descurtir' : 'curtir'} o post.`);
      setPosts(prevPosts =>
        prevPosts.map(p =>
          p.id === postId
            ? {
                ...p,
                user_liked: currentlyLiked,
                like_count: currentPost.like_count,
              }
            : p
        )
      );
    }
  }, [user, posts]);

  const handleNewPost = useCallback((newPost: PostRow, tagsPayload: any[]) => {
    if (!user) return;

    const tagsForFeed: PostTagRow[] = tagsPayload.map((tag) => ({
      post_id: newPost.id,
      tag_type: tag.type,
      game_id: tag.type === 'game' ? tag.value : null,
      tag_name: tag.type !== 'game' ? tag.value : null,
      game_name: tag.type === 'game' ? tag.game_name : null,
      id: Math.random(),
      created_at: new Date().toISOString(),
    }));

    const postForFeed: GetFeedResult = {
      ...newPost,
      author_username: user.username || null,
      author_full_name: user.full_name || null,
      author_avatar_url: user.avatar_url || null,
      like_count: 0,
      comment_count: 0,
      user_liked: false,
      tags: tagsForFeed,
    };

    setPosts(prev => [postForFeed, ...prev]);
  }, [user]);

  const handlePostDeleted = useCallback((deletedPostId: string) => {
    setPosts(prev => prev.filter(post => post.id !== deletedPostId));
  }, []);

  const handleCommentAdded = useCallback((postId: string) => {
    setPosts(prevPosts =>
      prevPosts.map(p =>
        p.id === postId
          ? { ...p, comment_count: (p.comment_count ?? 0) + 1 }
          : p
      )
    );
  }, []);

  const handlePostUpdated = useCallback((updatedPost: GetFeedResult) => {
    setPosts(prev =>
      prev.map(post =>
        post.id === updatedPost.id ? updatedPost : post
      )
    );
  }, []);

  if (loading && posts.length === 0 && !error) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-300">Carregando feed...</span>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="text-center py-10 text-red-600 dark:text-red-400">
        <p>{error}</p>
        <button onClick={() => fetchFeed(1, false)} className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700">
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showCreatePostForm && user && (
        <div className="mb-8 p-4 bg-white rounded-lg shadow-md dark:bg-gray-800 dark:border dark:border-gray-700">
          <CreatePostForm onPostCreated={handleNewPost} />
        </div>
      )}
      {posts.map((post, index) => (
        <div
          key={post.id}
          ref={enableInfiniteScroll && index === posts.length - 1 ? lastPostRef : null}
          className="post-item-wrapper"
        >
          <PostItem
            post={post}
            onToggleLike={handleToggleLike}
            onPostDeleted={handlePostDeleted}
            onPostUpdated={handlePostUpdated}
            onCommentAdded={handleCommentAdded}
            onTagClick={onTagClick}
          />
        </div>
      ))}

      {loadingMore && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      )}
    </div>
  );
};

export default FeedComponent; 